import {useMemo} from 'react';

import {usePersonalData} from 'presentation/hooks';
import {useMediaQueries} from 'presentation/pages/private/hooks';
import {useGetHealthCardQuery} from 'presentation/pages/private/health-card/queries/useGetHealthCardQuery';
import {useAccountDataStore} from 'presentation/pages/private/store/useAccountDataStateStore';
import {ContractTypeEnum, OperationStateTypesEnum} from 'presentation/constants';
import {translations} from 'presentation/translations-new';
import {formatMasks, formatText, getUserInfoFromToken} from 'presentation/utils';
import {useGetRequestPreviewProtocolPdf} from 'presentation/pages/private/refund/hooks/useGetRequestPreviewProtocolPdf';
import {useRequestPreviewFlowStore} from '../../../store/useRequestPreviewFlowStore';
import {SelectTypeOptionLabels} from '../../SelectTypeV2/types';

import {HolderDataItem} from '../types';

const finishedLabels =
	translations['pt-br'].privateFlow.refund.previewServiceType.finished;

function formatBRL(value: number): string {
	return new Intl.NumberFormat('pt-BR', {
		style: 'currency',
		currency: 'BRL',
	}).format(value);
}

export function useFinished(onBackToRefundMenu?: () => void) {
	const {isMobile} = useMediaQueries();
	const {
		downloadPdf,
		cancelDownload,
		DOWNLOAD_PDF_IS_LOADING,
		DOWNLOAD_PDF_IS_ERROR,
		setOperationState,
	} = useGetRequestPreviewProtocolPdf();
	const {
		finishPreviewParams,
		previewsAnalysisResult,
		selectedServiceTypeOption,
		isPdfExitWarningOpen,
		setIsPdfExitWarningOpen,
	} = useRequestPreviewFlowStore(state => state);


	const {beneficiary} = useAccountDataStore(store => store.accountData);
	const {nmSocialUsuarioC, nmUsuarioC, nmPlano, dtAdesaoC, tipoContratacao} = beneficiary;
	const {cdUsuario} = getUserInfoFromToken();
	const {currentPhone, currentEmail} = usePersonalData();
	const {data: healthCardInfo} = useGetHealthCardQuery();

	const isEnterprise = tipoContratacao === ContractTypeEnum.EMPRESARIAL;

	const holderData = useMemo((): HolderDataItem[] => {
		const items: HolderDataItem[] = [
			{labelKey: 'holderDataTitle', content: formatText(nmSocialUsuarioC || nmUsuarioC)},
			{labelKey: 'phoneLabel', content: formatMasks('phone', currentPhone ?? '')},
			{labelKey: 'emailLabel', content: currentEmail ?? '-'},
			{labelKey: 'cardCodeLabel', content: cdUsuario},
		];

		if (isEnterprise && healthCardInfo?.nomeEmpresa) {
			items.push({labelKey: 'companyLabel', content: healthCardInfo.nomeEmpresa});
		}

		items.push(
			{labelKey: 'planLabel', content: nmPlano},
			{labelKey: 'validityStartLabel', content: dtAdesaoC},
		);

		return items;
	}, [
		nmSocialUsuarioC, nmUsuarioC, currentPhone, currentEmail,
		cdUsuario, isEnterprise, healthCardInfo, nmPlano, dtAdesaoC,
	]);

	const analysisContent = previewsAnalysisResult?.content;

	const requestNumber = analysisContent?.request ?? '-';
	const protocolNumber = analysisContent?.protocol ?? '-';
	const estimatedValue =
		analysisContent?.totalReportedAmount != null
			? formatBRL(analysisContent.totalReportedAmount)
			: '-';
	const rawProcedureDescription =
		finishPreviewParams?.selectedProcedure?.description ?? selectedServiceTypeOption ?? '-';
	const selectedProcedureDescription =
		rawProcedureDescription === '-' ? '-' : formatText(rawProcedureDescription);
	const procedureSectionLabel =
		selectedServiceTypeOption === SelectTypeOptionLabels.CONSULTA
			? finishedLabels.procedureLabel
			: finishedLabels.selectedProcedureLabel;
	const pdfFileName =
		requestNumber && requestNumber !== '-'
			? `previa-de-reembolso-${requestNumber}.pdf`
			: 'previa-de-reembolso.pdf';

	function handleExportClick() {
		downloadPdf({protocol: protocolNumber, request: requestNumber});
	}

	function handleCloseErrorModal() {
		setOperationState(OperationStateTypesEnum.NONE);
	}

	function handleBackToRefundMenuClick() {
		if (DOWNLOAD_PDF_IS_LOADING) {
			setIsPdfExitWarningOpen(true);
			return;
		}
		onBackToRefundMenu?.();
	}

	function handleConfirmExit() {
		cancelDownload();
		setIsPdfExitWarningOpen(false);
		onBackToRefundMenu?.();
	}

	function handleCancelExit() {
		setIsPdfExitWarningOpen(false);
	}

	return {
		isMobile,
		holderData,
		exportLoading: DOWNLOAD_PDF_IS_LOADING,
		handleExportClick,
		errorModalOpen: DOWNLOAD_PDF_IS_ERROR,
		handleCloseErrorModal,
		isExitWarningModalOpen: isPdfExitWarningOpen,
		setIsExitWarningModalOpen: setIsPdfExitWarningOpen,
		handleBackToRefundMenuClick,
		handleConfirmExit,
		handleCancelExit,
		requestNumber,
		protocolNumber,
		estimatedValue,
		selectedProcedureDescription,
		procedureSectionLabel,
		pdfFileName,
	};
}
